import React, { useState, useEffect, useRef } from 'react';
import styles from './sections.module.css';

interface Fact {
  value: string;
  label: string;
  suffix?: string;
}

interface FactsSectionProps {
  facts: Fact[];
  image?: string;
}

const AnimatedCounter: React.FC<{ value: string; suffix?: string }> = ({ value, suffix }) => {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLSpanElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          animateValue();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  const animateValue = () => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    if (isNaN(numericValue)) {
      setDisplay(value);
      return;
    }

    const duration = 2000;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutQuart
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = Math.round(numericValue * eased);
      setDisplay(current.toLocaleString('nl-NL'));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  return (
    <span ref={ref} className={styles.factValue}>
      {display}{suffix && <span className={styles.factSuffix}>{suffix}</span>}
    </span>
  );
};

export const FactsSection: React.FC<FactsSectionProps> = ({ facts, image }) => {
  if (!facts || facts.length === 0) return null;

  return (
    <section id="facts" className={`${styles.section} ${styles.factsBg}`}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>Feiten & Cijfers</h2>
        {image && (
          <div className={styles.sectionImageDark}>
            <img src={image} alt="Feiten & Cijfers" />
          </div>
        )}
        <div className={styles.factsGrid}>
          {facts.map((fact, i) => (
            <div key={i} className={styles.factCard}>
              <AnimatedCounter value={fact.value} suffix={fact.suffix} />
              <span className={styles.factLabel}>{fact.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
